import { useState } from 'react';
import TurnstileWidget from '../components/TurnstileWidget';
import { siteConfig } from '../config/siteConfig';
import { validateContact } from '../lib/formValidation';
import { sendContactMessage } from '../services/supportApi';

const INITIAL_VALUES = {
  fullName: '',
  email: '',
  subject: '',
  message: '',
  company: '',
};

const subjects = [
  ['SUPPORT', 'Destek'],
  ['PRIVACY', 'Gizlilik'],
  ['ACCOUNT_DELETION', 'Hesap Silme'],
  ['FEEDBACK', 'Öneri / Geri Bildirim'],
  ['OTHER', 'Diğer'],
];

export default function ContactPage() {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [widgetKey, setWidgetKey] = useState(0);

  const update = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    const nextErrors = validateContact({ ...values, turnstileToken });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const response = await sendContactMessage({
        ...values,
        turnstileToken,
      });
      setResult({
        success: true,
        message: response.message,
        requestId: response.requestId,
      });
      setValues(INITIAL_VALUES);
      setTurnstileToken('');
      setWidgetKey((current) => current + 1);
    } catch (error) {
      setErrors(error.fieldErrors || {});
      setResult({ success: false, message: error.message });
      setTurnstileToken('');
      setWidgetKey((current) => current + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="page-section">
      <div className="shell support-layout">
        <div className="support-intro">
          <span className="eyebrow">İletişim ve destek</span>
          <h1>Nasıl yardımcı olabiliriz?</h1>
          <p>
            Destek, gizlilik, hesap işlemleri veya önerilerin için formu
            kullanabilirsin. Mesajını doğru ekibe yönlendireceğiz.
          </p>
          <div className="response-promise">
            <span aria-hidden="true">◷</span>
            <div>
              <strong>Dönüş hedefimiz</strong>
              <p>Mesajlara en geç 3 iş günü içinde dönüş yapmayı hedefliyoruz.</p>
            </div>
          </div>
          {siteConfig.supportEmail && (
            <p className="direct-email">
              E-posta: <a href={'mailto:' + siteConfig.supportEmail}>{siteConfig.supportEmail}</a>
            </p>
          )}
        </div>

        <form className="form-card" onSubmit={submit} noValidate>
          <div className="form-grid">
            <label className="field">
              <span>Ad soyad <small>İsteğe bağlı</small></span>
              <input
                name="fullName"
                value={values.fullName}
                onChange={update}
                autoComplete="name"
                maxLength="100"
              />
            </label>
            <label className="field">
              <span>E-posta</span>
              <input
                name="email"
                type="email"
                value={values.email}
                onChange={update}
                autoComplete="email"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? 'contact-email-error' : undefined}
                required
              />
              {errors.email && (
                <span className="field-error" id="contact-email-error">
                  {errors.email}
                </span>
              )}
            </label>
          </div>
          <label className="field">
            <span>Konu</span>
            <select
              name="subject"
              value={values.subject}
              onChange={update}
              aria-invalid={Boolean(errors.subject)}
              aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
              required
            >
              <option value="">Konu seçin</option>
              {subjects.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {errors.subject && (
              <span className="field-error" id="contact-subject-error">{errors.subject}</span>
            )}
          </label>
          <label className="field">
            <span>Mesaj</span>
            <textarea
              name="message"
              value={values.message}
              onChange={update}
              rows="7"
              maxLength="3000"
              aria-invalid={Boolean(errors.message)}
              aria-describedby={errors.message ? 'contact-message-error' : undefined}
              required
            />
            <span className="field-counter">{values.message.length} / 3000</span>
            {errors.message && (
              <span className="field-error" id="contact-message-error">{errors.message}</span>
            )}
          </label>
          <label className="honeypot" aria-hidden="true">
            Şirket
            <input
              name="company"
              value={values.company}
              onChange={update}
              tabIndex="-1"
              autoComplete="off"
            />
          </label>
          <TurnstileWidget
            key={widgetKey}
            onToken={setTurnstileToken}
            error={errors.turnstileToken}
          />
          {result && (
            <div
              className={'form-result ' + (result.success ? 'is-success' : 'is-error')}
              role="status"
            >
              <strong>{result.message}</strong>
              {result.requestId && <span>Başvuru numaranız: {result.requestId}</span>}
            </div>
          )}
          <button className="button button-primary form-submit" disabled={submitting}>
            {submitting ? 'Gönderiliyor…' : 'Mesajı gönder'}
          </button>
          <p className="form-privacy">
            Formu göndererek bilgilerinin destek talebini yanıtlamak amacıyla
            işlenmesini kabul edersin. Ayrıntılar için{' '}
            <a href="/privacy">Gizlilik Politikası</a>.
          </p>
        </form>
      </div>
    </section>
  );
}
